import { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Elegant 12-hour time picker — pick Hour (1-12), Minute (00/15/30/45), Period (AM/PM).
 * Stores value as 24-hour "HH:MM" string for backend compatibility.
 *
 * Usage:
 *   <TimePicker value={"14:30"} onChange={(v) => setTime(v)} />
 */
export default function TimePicker({ value, onChange, placeholder = "Select time", testIdPrefix = "tp", className = "", minuteStep = 15 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Parse value into hour12, minute, period
  const { hour12, minute, period } = parseValue(value);

  useEffect(() => {
    // Use pointerdown so iOS Safari touch taps also close the popup reliably.
    // Capture phase is critical: without `capture`, Radix Dialog's own
    // pointerdown listener can intercept the event before ours runs, causing
    // the picker to stay open or eat the first tap on mobile.
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("pointerdown", onDoc, true);
    return () => document.removeEventListener("pointerdown", onDoc, true);
  }, [open]);

  const setHour = (h) => onChange(format24(h, minute, period));
  const setMinute = (m) => onChange(format24(hour12, m, period));
  const setPeriod = (p) => onChange(format24(hour12, minute, p));

  const display = value ? `${hour12}:${String(minute).padStart(2, "0")} ${period}` : "";
  const minutes = []; for (let m = 0; m < 60; m += minuteStep) minutes.push(m);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full h-11 px-3 bg-zen-surface rounded-md text-left flex items-center gap-2 text-sm hover:bg-zen-surface/70"
        data-testid={`${testIdPrefix}-trigger`}>
        <Clock className="w-4 h-4 text-zen-muted flex-shrink-0" />
        <span className={display ? "text-zen-ink font-medium" : "text-zen-muted"}>{display || placeholder}</span>
      </button>
      {open && (
        <div className="absolute z-50 left-0 mt-1 bg-white border border-zen-border rounded-xl shadow-xl p-3 min-w-[240px]" data-testid={`${testIdPrefix}-popup`}>
          <div className="flex gap-2">
            <Column label="Hour" testId={`${testIdPrefix}-hours`}>
              {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
                <Cell key={h} active={hour12 === h} onClick={() => setHour(h)} testId={`${testIdPrefix}-h-${h}`}>
                  {h}
                </Cell>
              ))}
            </Column>
            <Column label="Min" testId={`${testIdPrefix}-mins`}>
              {minutes.map((m) => (
                <Cell key={m} active={minute === m} onClick={() => setMinute(m)} testId={`${testIdPrefix}-m-${m}`}>
                  {String(m).padStart(2, "0")}
                </Cell>
              ))}
            </Column>
            <Column label="Period" testId={`${testIdPrefix}-period`}>
              {["AM", "PM"].map((p) => (
                <Cell key={p} active={period === p} onClick={() => setPeriod(p)} testId={`${testIdPrefix}-${p.toLowerCase()}`}>
                  {p}
                </Cell>
              ))}
            </Column>
          </div>
          <div className="flex justify-end mt-2 pt-2 border-t border-zen-border">
            <button onClick={() => setOpen(false)} className="px-3 h-8 rounded-full text-xs bg-zen-primary text-white" data-testid={`${testIdPrefix}-done`}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Column({ label, testId, children }) {
  return (
    <div className="flex-1" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zen-muted mb-1.5 text-center font-semibold">{label}</div>
      <div className="max-h-48 overflow-y-auto flex flex-col gap-1 pr-1">{children}</div>
    </div>
  );
}

function Cell({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full py-1.5 rounded-md text-sm font-medium transition-all ${active ? "bg-zen-primary text-white shadow" : "hover:bg-zen-surface text-zen-ink"}`}
      data-testid={testId}>
      {children}
    </button>
  );
}

function parseValue(v) {
  if (!v) return { hour12: 9, minute: 0, period: "AM" };
  const [hStr, mStr] = v.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { hour12, minute: m, period };
}

function format24(hour12, minute, period) {
  let h = hour12 % 12;
  if (period === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}
